import { compact, flatten, uniqBy } from 'lodash';
import { CornerDownRight, Split } from 'lucide-react';
import { useRouter } from 'next/router';
import { useRecoilValue } from 'recoil';

import { Label } from '@/components/inputs';
import { Toggle } from '@/components/inputs/toggle';
import { Text } from '@/components/text';
import { TrafficLight } from '@/components/traffic-light';
import { AVOID, ROUTING_SEARCH_URL, SUEZ_CANAL_ID } from '@/lib/constants';
import { OptionModel } from '@/lib/models/option/types';
import { sortOptionsByDuration } from '@/lib/models/option/utils/sort-options-by-duration';
import { RouteModel } from '@/lib/models/route/types';
import { getDoesRouteTransitSuez } from '@/lib/models/route/utils/get-does-route-transit-suez';
import { routeSearchDestinationState, routeSearchOriginState } from '@/state/routes';
import { cargoSearchParamsState, searchParamsSelectorState } from '@/state/search';
import { formatDuration } from '@/utils/format';
import { searchStateToQueryParams } from '@/utils/route';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface RouteTransitDurationtProps {
  route: RouteModel;
}

const countViaByLocationId = (vias: { locationId: string }[]): Record<string, number> => {
  return vias.reduce(
    (accum, location) => {
      const count = { ...accum };
      count[location.locationId] = (count[location.locationId] || 0) + 1;
      return count;
    },
    {} as Record<string, number>,
  );
};

const getAverageOptionDuration = (options: OptionModel[]): number => {
  return options.length > 0 ? options.reduce((sum, opt) => sum + opt.totalDurationSeconds, 0) / options.length : 0;
};

const RouteTransitDurationPopout = ({ route }: RouteTransitDurationtProps) => {
  const router = useRouter();

  const origin = useRecoilValue(routeSearchOriginState);
  const destination = useRecoilValue(routeSearchDestinationState);
  const cargoParams = useRecoilValue(cargoSearchParamsState);
  const searchParams = useRecoilValue(searchParamsSelectorState);

  const { mainOptions, primaryMode, start, end } = route;

  trackUser.event('TransitDuration: Popout open', { mode: primaryMode });

  const hasMoreThanOneOption = mainOptions.length > 1;
  const startEndDuration = (start?.durationSeconds || 0) + (end?.durationSeconds || 0);
  const averageDurationSeconds = getAverageOptionDuration(mainOptions);
  const fastestOption = sortOptionsByDuration(mainOptions)[0];
  const slowestOption = sortOptionsByDuration(mainOptions, false)[0];
  const alternativeCount = mainOptions.length;

  // Handle via Waypoints (ocean only)
  const viaLocations = flatten(compact(mainOptions.map((opt) => opt.via)));
  const uniqViaLocations = uniqBy(viaLocations, 'locationId');
  const viaCounts = countViaByLocationId(viaLocations);
  const anyRoutesGoViaSuez = getDoesRouteTransitSuez(mainOptions);
  const avoidSuezEnabled = !!searchParams.avoid?.find((location) => location.value === SUEZ_CANAL_ID);

  const handleToggleSuez = () => {
    const updatedSearchParams = {
      ...searchParams,
      [AVOID]: [...(searchParams.avoid || []), { label: 'Suez Canal', value: SUEZ_CANAL_ID }],
    };

    // Event tracking
    trackUser.event('TransitDuration: Avoid Suez');

    const query = searchStateToQueryParams({
      origin,
      destination,
      advancedSearchParams: updatedSearchParams,
      cargoParams,
    });
    router.push({ pathname: ROUTING_SEARCH_URL, query });
  };

  const { base, content, preText, primaryText, stats, statsBlock, alert, alertLabel, viaList, portLocation } = styles();

  return (
    <article className={base()}>
      <div className={content()}>
        <div>
          <div className={preText()}>Overview</div>
          <div className="mt-1 flex items-center gap-1">
            <Split className="size-3" />
            <Text variant="secondary" className="mb-0 font-medium">
              {alternativeCount === 1 ? 'Only 1 active service on this route' : `${alternativeCount} possible routes`}
            </Text>
          </div>
          {uniqViaLocations.length > 0 && (
            <div className={viaList()}>
              {uniqViaLocations.map((via) => (
                <div key={via.locationId} className={portLocation()}>
                  <CornerDownRight className="mt-0.5 size-3" />
                  <Text variant="secondary" className="mb-0">
                    {viaCounts[via.locationId]} {via.long}
                  </Text>
                  {via.locationId === SUEZ_CANAL_ID && (
                    <span className="ml-1 mt-1">
                      <TrafficLight color="red" showAlertIcon />
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {hasMoreThanOneOption ? (
          <div className={stats()}>
            <div className={statsBlock()}>
              <div className={preText()}>Fastest</div>
              <div className={primaryText()}>
                {formatDuration(fastestOption.totalDurationSeconds + startEndDuration)}
              </div>
            </div>
            <div className={statsBlock()}>
              <div className={preText()}>Slowest</div>
              <div className={primaryText()}>
                {formatDuration(slowestOption.totalDurationSeconds + startEndDuration)}
              </div>
            </div>

            <div className={statsBlock()}>
              <div className={preText()}>Average</div>
              <div className={primaryText()}>{formatDuration(averageDurationSeconds + startEndDuration)}</div>
            </div>
          </div>
        ) : (
          <div className={stats({ className: 'grid-cols-1' })}>
            <div className={statsBlock()}>
              <div className={preText()}>Typical transit duration</div>
              <div className={primaryText()}>
                {formatDuration(fastestOption.totalDurationSeconds + startEndDuration)}
              </div>
            </div>
          </div>
        )}
      </div>
      {anyRoutesGoViaSuez && (
        <div className={alert()}>
          <div>
            <Text variant="sm" className="mb-0 leading-snug">
              If you want to <b>avoid the Suez Canal</b> and minimise delays and disruptions, try filtering it from
              search results.
            </Text>
            <div className="mt-3 flex">
              <Toggle name="suez" legend="Suez" on={avoidSuezEnabled} onChange={handleToggleSuez} />
              <div className="flex items-center gap-1">
                <Label className={alertLabel()} htmlFor="suez">
                  Remove Suez canal results
                </Label>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

const styles = tv({
  slots: {
    base: 'text-left',
    content: 'relative block space-y-4 p-4 text-baseSm',
    preText: 'text-xs text-grey-500',
    primaryText: 'text-baseSm font-medium',
    stats: 'grid grid-cols-3 gap-2 border-t border-grey-200 pt-4',
    statsBlock: 'border-r border-grey-200 leading-snug last:border-0',
    viaList: 'mt-2 space-y-1 pl-4',
    portLocation: 'flex items-start gap-1',
    alert:
      '-m-px rounded-b-md border border-states-warning-borderColor/50 bg-states-warning-backgroundColor p-4 leading-snug [&_b]:font-medium',
    alertLabel: '!m-0 text-sm capitalize text-text-primary',
  },
});

export { RouteTransitDurationPopout };
